import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  Modal,
  Stack,
  Typography,
} from "@mui/material";
import { CardElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { StripeCardElementChangeEvent } from "@stripe/stripe-js";
import { ChangeEvent, FormEvent, useCallback, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import { TAGS } from "src/api/constants";
import {
  paymentInformationApi,
  useSetDefaultPaymentMethodMutation,
} from "src/api/services/paymentInformationApi";
import { isString } from "src/utils/isString";
import { useAppDispatch } from "src/api/hooks";
import { useCreateSetupIntentMutation } from "src/api/services/paymentsApi";
import { PatientProfile } from "src/api/types";

const styles = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "100%",
  maxWidth: 600,
  bgcolor: "background.paper",
  borderRadius: "10px",
  boxShadow: 24,
  p: 4,
};

interface Props {
  onClose: () => void;
  open: boolean;
  patient: PatientProfile;
}

const AddNewPayment = ({ open, onClose, patient }: Props) => {
  const stripe = useStripe();
  const elements = useElements();
  const [markDefault, setMarkDefault] = useState(false);
  const [error, setError] = useState("");
  const [cardCompleted, setCardCompleted] = useState(false);
  const dispatch = useAppDispatch();
  const [isLoading, setSubmitLoading] = useState(false);
  const [createSetUpIntent] = useCreateSetupIntentMutation();
  const [setupDefaultPayment] = useSetDefaultPaymentMethodMutation();

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) =>
      setMarkDefault(event.target.checked),
    [],
  );

  const handleCardChange = useCallback(
    (event: StripeCardElementChangeEvent) => {
      setCardCompleted(event.complete);
    },
    [],
  );

  const handleSubmit = useCallback(
    async (event: FormEvent) => {
      event.preventDefault();

      if (!stripe || !elements) return;

      if (patient.stripe_customer_id == null) return;

      const cardElement = elements.getElement(CardElement);

      if (!cardElement) return;

      try {
        setSubmitLoading(true);

        const { data } = await createSetUpIntent({
          payment_customer_id: patient.stripe_customer_id,
        });

        const clientSecret = data?.clientSecret;

        if (!clientSecret) {
          setError("Oops, something went wrong");
          return;
        }

        const result = await stripe.confirmCardSetup(clientSecret, {
          payment_method: {
            card: cardElement,
            billing_details: {
              name: `${patient.profile.first_name} ${patient.profile.last_name}`,
            },
          },
        });

        if (result.error) {
          setError(result.error.message || "Error confirming card");
          return;
        }

        if (
          markDefault === true &&
          isString(result.setupIntent.payment_method)
        ) {
          setupDefaultPayment({
            customerId: patient.stripe_customer_id,
            paymentMethodId: result.setupIntent.payment_method,
          });
        }

        dispatch(
          paymentInformationApi.util.invalidateTags([TAGS.PAYMENT_METHODS]),
        );
      } catch {
        setError("Oops, something went wrong. Please try again.");
      } finally {
        setSubmitLoading(false);
        onClose();
      }
    },
    [
      createSetUpIntent,
      dispatch,
      elements,
      markDefault,
      onClose,
      patient.profile.first_name,
      patient.profile.last_name,
      patient.stripe_customer_id,
      setupDefaultPayment,
      stripe,
    ],
  );

  return (
    <Modal open={open} onClose={onClose}>
      <Stack sx={styles} gap="16px" component="form" onSubmit={handleSubmit}>
        <Stack direction="row" justifyContent="space-between">
          <Stack>
            <Typography variant="h6" fontWeight={600}>
              Add new card
            </Typography>
            <Typography variant="subtitle1">
              Enter the patient's credit/debit card details
            </Typography>
          </Stack>
          <IconButton onClick={onClose} sx={{ alignSelf: "baseline" }}>
            <CloseIcon />
          </IconButton>
        </Stack>

        <Box
          role="form"
          sx={{
            border: "1px solid var(--Secondary-200, #DCE4E8)",
            borderRadius: "10px",
            p: 3,
          }}
        >
          <CardElement
            onChange={handleCardChange}
            options={{
              hidePostalCode: true,
              style: {
                base: {
                  fontSize: "16px",
                  "::placeholder": {
                    color: "#1B1B1B",
                  },
                },
              },
            }}
          />
          {error && <Typography sx={{ color: "red" }}>{error}</Typography>}
        </Box>
        <FormControlLabel
          control={<Checkbox checked={markDefault} onChange={handleChange} />}
          label="Make default payment method"
        />
        <Stack direction="row" gap="16px" alignSelf="end">
          <Button variant="alternate" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="contained"
            loading={isLoading}
            type="submit"
            disabled={!cardCompleted}
          >
            Submit
          </Button>
        </Stack>
      </Stack>
    </Modal>
  );
};

export default AddNewPayment;
